<template>
    <div class="segments-grid-group position-relative">
        <v-inline-loader v-if="loadingSegments"/>
        <v-repeater :list="segments" table-class="table-layout-fixed">
            <template slot="headers">
                <th>Title</th>
                <th>Type</th>
                <th>Owner</th>
                <th>Shared with</th>
                <th>Visibility</th>
                <th>Starred</th>
                <th></th>
            </template>
            <template slot="iteratee" slot-scope="{item: segment}">
                <td fs-block>
                    <router-link
                        :to="{name: 'lists', params: {segmentId: segment.id}}"
                        v-if="isListsSegments"
                    >
                        {{segment.data.name | truncate({limit: 26, dots: true})}}
                    </router-link>
                    <span v-else>
                        {{segment.data.name | truncate({limit: 26, dots: true})}}
                    </span>
                </td>
                <td fs-block>{{segment.data.type | unKebabCase}}</td>
                <td fs-block>{{segment.owner.name | coverEmpty}}</td>
                <td fs-block>
                    <template v-if="segment.isOwner(userId) && $auth.gridSegments.edit.isAllowed && !segment.isReadOnly">
                        <r-button class="inline-xs" type="link" @click="setEditingSegment(segment)">
                            {{segment.sharedWith}}
                        </r-button>
                    </template>
                    <template v-else>
                        {{segment.sharedWith}}
                    </template>
                </td>
                <td fs-block>
                    <div class="field">
                        <label class="field-switch-body" :for="segment.id">
                            <input
                                type="checkbox"
                                :id="segment.id"
                                :disabled="segment.isReadOnly"
                                class="field-switch"
                                :checked="segment.isVisible"
                                @click.stop="toggleIsVisibility(segment)"
                            >
                            <span class="field-switch-handle"></span>
                        </label>
                    </div>
                </td>
                <td fs-block>
                    <v-icon-button
                        type="basic"
                        :disabled="segment.isReadOnly"
                        @click="toggleIsStarred(segment)">
                        <v-icon :icon="getStarredIcon(segment)"></v-icon>
                    </v-icon-button>
                </td>
                <td fs-block>
                    <v-icon-button
                        size="small"
                        type="basic"
                        tooltip="Delete"
                        @click="showDeleteSegmentConfirmation(segment)"
                        v-if="(isImpersonating && !segment.isReadOnly) || (segment.isOwner(userId) && $auth.gridSegments.edit.isAllowed && !segment.isReadOnly)">
                        <v-icon icon="trash"/>
                    </v-icon-button>
                </td>
            </template>
            <template #no-content>
                <div>There are no segments for this group</div>
            </template>
        </v-repeater>

        <segment-share-with-modal
                :show="showSharingModal"
                v-model="segmentBeingEdited"
                @update="saveEditedSegment"
                @close="showSharingModal = false"/>

        <v-modal v-if="confirmDeletion" title="Delete Segment" size="small" @close="confirmDeletion = false">
            <div slot="content">
                <p>Are you sure you want to delete the segment "{{segmentBeingEdited.data.name}}"?</p>
            </div>
            <template slot="actions">
                <r-button type="danger" @click="triggerDelete">Delete Segment</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import SegmentDataModel from '@/common/models/segment-data-model';
    import vModal from '@/components/modal/v-modal';
    import vInlineLoader from '@/components/v-inline-loader';
    import vRepeater from '@/components/repeater/repeater';
    import {
        vIcon, vIconButton, vRadio, vSelect,
    } from '@/components/form';
    import {mapActions, mapGetters} from 'vuex';

    export default {
        name: 'SegmentGridGroup',
        components: {
            vModal,
            vInlineLoader,
            vRepeater,
            vIcon,
            vIconButton,
            vRadio,
            vSelect,
            SegmentShareWithModal: () => import('@/modules/segments/components/segment-shared-with-modal'),
        },
        data() {
            return {
                showSharingModal: false,
                confirmDeletion: false,
                segmentBeingEdited: {},
                segments: [],
                loadingSegments: false,
            };
        },
        props: {
            category: {
                type: String,
                required: true,
            },
            page: {
                type: String,
                required: true,
            },
        },
        computed: {
            ...mapGetters({
                segmentCollection: 'segments/segmentCollection',
                userId: 'user/userId',
                isImpersonating: 'user/isImpersonating',
            }),
            isListsSegments() {
                return this.page === SegmentDataModel.Pages.lists;
            },
        },
        mounted() {
            this.segments = this.segmentCollection.pages[this.page].categories[this.category].segments;
        },
        methods: {
            ...mapActions({
                fetchSegment: 'segments/fetch',
                updateSegment: 'segments/update',
                deleteSegment: 'segments/delete',
                toggleVisibility: 'segments/toggleVisibility',
                toggleStarred: 'segments/toggleStarred',
            }),
            async toggleIsVisibility(segment) {
                try {
                    this.loadingSegments = true;
                    await this.toggleVisibility(segment);
                    segment.isVisible = !segment.isVisible;
                } catch (err) {
                    this.$toast.fromError(err, 'Unable to toggle the visibility of this segment.');
                } finally {
                    this.loadingSegments = false;
                }
            },
            async toggleIsStarred(segment) {
                try {
                    this.loadingSegments = true;
                    await this.toggleStarred(segment);
                    segment.isStarred = !segment.isStarred;
                } catch (err) {
                    this.$toast.fromError(err, 'Unable to toggle the starred state of this segment.');
                } finally {
                    this.loadingSegments = false;
                }
            },
            getStarredIcon({isStarred}) {
                // TODO missing starred/un-starred icons
                return isStarred ? 'star' : 'star-empty';
            },
            setEditingSegment(segment) {
                this.segmentBeingEdited = segment;
                this.showSharingModal = true;
            },
            showDeleteSegmentConfirmation(segment) {
                this.confirmDeletion = true;
                this.segmentBeingEdited = segment;
            },
            async triggerDelete() {
                try {
                    this.loadingSegments = true;
                    this.confirmDeletion = false;
                    const {id} = this.segmentBeingEdited;
                    await Promise.all([
                        await this.deleteSegment(id),
                        await this.fetchSegment(this.$auth),
                    ]);
                    this.segments = this.segmentCollection.pages[this.page].categories[this.category].segments;
                    this.segmentBeingEdited = {};
                    this.$toast.positive(`Segment successfully deleted`);
                } catch (error) {
                    this.$toast.fromError(error, `Segment could not be deleted please try again or contact support`);
                } finally {
                    this.loadingSegments = false;
                }
            },
            async saveEditedSegment(segment) {
                await this.updateSegment(segment);
                this.segments = this.segmentCollection.pages[this.page].categories[this.category].segments;
            },
        },
    };
</script>
